import { debounce, head, last, maxBy } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { Link, matchPath, useHistory, useLocation } from "react-router-dom";
import ChevronUpIcon from "../../components/icons/ChevronUpIcon";
import CloseIcon from "../../components/icons/CloseIcon";
import { ApiResponse, fetchApi } from "../../utils/api";
import getNearPoint from "../../utils/getNearPoint";
import { pointDistanceMemoized } from "../../utils/memoizedMethods";
import useMapCenter, { Coordinates } from "../../utils/useMapCenter";
import useMapConfig, {
  invalidColor,
  validColor
} from "../../utils/useMapConfig";
import usePrevious from "../../utils/usePrevious";

type ContextValue = {
  startPoint: Coordinates | undefined;
  endPoint: Coordinates | undefined;
  startHeight: number;
  endHeight: number;
  pointsData: {
    firstStartHeight: number;
    firstEndHeight: number;
    firstStartIndex: number;
    firstEndIndex: number;
  };
  setStartHeight: React.Dispatch<React.SetStateAction<number>>;
  setEndHeight: React.Dispatch<React.SetStateAction<number>>;
  clearLineOfSight: boolean | undefined;
  setStartPoint: (value: Coordinates | undefined) => void;
  setEndPoint: (value: Coordinates | undefined) => void;
  clearPoints: () => void;
  chartData:
    | {
        elevation: number;
        line: number | undefined;
      }[]
    | undefined;
  isMinimized: boolean;
  setIsMinimized: React.Dispatch<React.SetStateAction<boolean>>;
  renderMinimizedComponent: any;
  distance: number | undefined;
  isEditingStart: boolean;
  isEditingEnd: boolean;
  setIsEditingStart: (value: boolean) => void;
  setIsEditingEnd: (value: boolean) => void;
  lineOfSightLineColor: string;
};

const LineOfSightContext = React.createContext<ContextValue | undefined>(
  undefined
);

interface Props {}

export const LineOfSightProvider: React.FC<Props> = (props) => {
  const location = useLocation();
  const [startPoint, setStartPoint] = useState<Coordinates | undefined>(
    undefined
  );
  const [distanceUnit, getAppearanceDistanceValue, getDistanceValueInMeters] =
    useMapConfig((state) => [
      state.distanceUnit,
      state.getAppearanceDistanceValue,
      state.getDistanceValueInMeters
    ]);
  const [endPoint, setEndPoint] = useState<Coordinates | undefined>(undefined);
  const [startHeight, setStartHeight] = useState(
    getAppearanceDistanceValue(defaultHeight, false)
  );
  const [endHeight, setEndHeight] = useState(
    getAppearanceDistanceValue(defaultHeight, false)
  );
  const [isEditingStart, setIsEditingStart] = useState<boolean>(false);
  const [isEditingEnd, setIsEditingEnd] = useState<boolean>(false);
  const [distance, setDistance] = useState<number | undefined>();
  const [elevationData, setElevationData] = useState<
    ElevationDataResponse | undefined
  >();
  const center = useMapCenter((state) => state.center);
  const [setCenter] = useMapCenter((state) => [state.setCenter]);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const history = useHistory();

  const prevDistanceUnit = usePrevious(distanceUnit);
  useEffect(() => {
    if (prevDistanceUnit !== distanceUnit) {
      setStartHeight(getAppearanceDistanceValue(startHeight, false));
      setEndHeight(getAppearanceDistanceValue(endHeight, false));
    }
  }, [
    distanceUnit,
    endHeight,
    getAppearanceDistanceValue,
    prevDistanceUnit,
    startHeight
  ]);

  const clearPoints = useCallback(() => {
    setStartPoint(undefined);
    setEndPoint(undefined);
    setElevationData(undefined);
    setIsMinimized(false);
    setIsEditingEnd(false);
    setIsEditingStart(false);
    setStartHeight(defaultHeight);
    setEndHeight(defaultHeight);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchLineOfSight = useCallback(
    debounce(async (start: Coordinates, end: Coordinates): Promise<void> => {
      if (controller) controller.abort();

      controller = new AbortController();
      const signal = controller.signal;

      try {
        if (!start || !end) return;

        const distance = pointDistanceMemoized(start, end);

        const distanceBetweenPoints = Math.max(
          minimumDistanceBetweenElevationPoints,
          Math.round(distance / targetNumberOfElevationPoints)
        );

        const response = await fetchApi(
          `/api/los/${start[1]},${start[0]}/${end[1]},${end[0]}/${distanceBetweenPoints}`,
          { signal }
        );

        const apiResponse: ApiResponse<ElevationDataResponse> =
          await response.json();

        if (apiResponse.status) {
          setElevationData(apiResponse.data);
        }
      } catch {}
    }, 250),
    []
  );

  useEffect(() => {
    if (isEditingEnd && endPoint) {
      setEndPoint(center);
    }
  }, [center, endPoint, isEditingEnd]);

  useEffect(() => {
    if (isEditingStart && endPoint) setStartPoint(center);
  }, [center, endPoint, isEditingStart]);

  useEffect(() => {
    if (startPoint && endPoint) {
      setDistance(pointDistanceMemoized(startPoint, endPoint));
    }
  }, [startPoint, endPoint]);

  useEffect(() => {
    if (startPoint && !endPoint) {
      const point = getNearPoint(startPoint, 2);

      setCenter(point);
      setEndPoint(point);
    }
  }, [endPoint, setCenter, startPoint]);

  const [chartData, clearLineOfSight, pointsData] = useMemo(() => {
    if (!elevationData || elevationData.data.length < 2) {
      return [
        undefined,
        undefined,
        {
          firstStartHeight: 0,
          firstEndHeight: 0,
          firstStartIndex: 0,
          firstEndIndex: 0
        }
      ];
    }

    const elevationStartPoint = head(
      elevationData.data.filter((item) => item.loc === "m")
    );

    const elevationEndPoint = last(
      elevationData.data.filter((item) => item.loc === "m")
    );

    if (!elevationStartPoint || !elevationEndPoint) {
      return [
        undefined,
        undefined,
        {
          firstStartHeight: 0,
          firstEndHeight: 0,
          firstStartIndex: 0,
          firstEndIndex: 0
        }
      ];
    }

    const slope =
      (elevationEndPoint.elevation +
        getDistanceValueInMeters(startHeight) -
        (elevationStartPoint.elevation + getDistanceValueInMeters(endHeight))) /
      (elevationEndPoint.distance - elevationStartPoint.distance);

    const intercept =
      elevationStartPoint.elevation +
      getDistanceValueInMeters(endHeight) -
      slope * elevationStartPoint.distance;

    let isClearLineOfSight = true;

    const highestElevationPoint =
      maxBy(elevationData.data, "elevation")?.elevation || 1;

    const dataPoints = elevationData.data.map(
      ({ elevation, distance, loc }, idx) => {
        const line = slope * distance + intercept;

        if (isClearLineOfSight && line <= elevation && loc === "m")
          isClearLineOfSight = false;

        return {
          elevation: elevation,
          line: loc !== "m" ? undefined : line,
          heightLimitLine: idx === 0 ? highestElevationPoint * 2 : undefined
        };
      }
    );

    const firstStartHeight = slope * elevationEndPoint.distance + intercept;
    const firstEndHeight = slope * elevationStartPoint.distance + intercept;

    return [
      dataPoints.reverse(),
      isClearLineOfSight,
      {
        firstStartHeight,
        firstEndHeight,
        firstStartIndex: dataPoints.findIndex(
          (item) => item.line === firstStartHeight
        ),
        firstEndIndex:
          last(
            dataPoints
              .map((item, index) => (item.line === firstEndHeight ? index : ""))
              .filter(String)
          ) || 0
      }
    ];
  }, [elevationData, endHeight, getDistanceValueInMeters, startHeight]);

  useEffect(() => {
    if (!startPoint || !endPoint) return;

    setElevationData(undefined);
    debouncedFetchLineOfSight(startPoint, endPoint);
  }, [endPoint, startPoint, debouncedFetchLineOfSight]);

  const pathMatch = matchPath<{
    tab: "hotspots" | "locations" | "hexes" | "contacts" | "installs";
  }>(location.pathname, {
    path: "/:tab"
  });

  const renderMinimizedComponent = useMemo(() => {
    return (
      <div className="absolute bottom-0 inset-x-0 z-10">
        <nav className="bg-gray-50 dark:bg-gray-800 px-4 py-3 flex items-center justify-between sm:px-6 border-t border-gray-200 dark:border-gray-500 rounded-lg">
          <div className="flex-1 flex justify-between items-center">
            <p className="text-sm text-gray-700 dark:text-gray-50 sm:text-xl">
              Line Of Sight
            </p>
            <div className="flex">
              <Link
                replace
                to={`/${pathMatch?.params.tab}/line-of-sight`}
                onClick={() => setIsMinimized(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400"
              >
                <ChevronUpIcon className="h-5 w-5" />
              </Link>

              <span
                onClick={() => {
                  clearPoints();
                  history.push(`/${pathMatch?.params.tab}`);
                }}
                className="ml-3 cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400 dark:disabled:hover:border-gray-700 dark:disabled:hover:bg-gray-900 dark:disabled:hover:text-gray-400"
              >
                <CloseIcon className="h-5 w-5" />
              </span>
            </div>
          </div>
        </nav>
      </div>
    );
  }, [clearPoints, history, pathMatch?.params.tab]);

  const lineOfSightLineColor = useMemo(() => {
    if (clearLineOfSight == null) return "rgb(156, 163, 175)";

    return clearLineOfSight ? validColor : invalidColor;
  }, [clearLineOfSight]);

  const value = useMemo(
    () => ({
      startPoint,
      endPoint,
      startHeight,
      endHeight,
      setStartHeight,
      setEndHeight,
      clearLineOfSight,
      setStartPoint,
      setEndPoint,
      clearPoints,
      chartData,
      isMinimized,
      setIsMinimized,
      renderMinimizedComponent,
      distance,
      isEditingStart,
      isEditingEnd,
      setIsEditingStart,
      setIsEditingEnd,
      lineOfSightLineColor,
      pointsData
    }),
    [
      startPoint,
      endPoint,
      startHeight,
      endHeight,
      clearLineOfSight,
      clearPoints,
      chartData,
      isMinimized,
      renderMinimizedComponent,
      distance,
      isEditingStart,
      isEditingEnd,
      lineOfSightLineColor,
      pointsData
    ]
  );

  return <LineOfSightContext.Provider value={value} {...props} />;
};

export const useLineOfSight = (): ContextValue => {
  const context = useContext(LineOfSightContext);
  if (context === undefined) {
    throw new Error(
      "useLineOfSight must be used within an LineOfSightProvider"
    );
  }
  return context;
};

//
// Utils
//

export const defaultHeight = 10;
const minimumDistanceBetweenElevationPoints = 50;
const targetNumberOfElevationPoints = 200;

let controller: AbortController | null = null;
export interface ElevationDataPoint {
  elevation: number;
  lat: number;
  lng: number;
  distance: number;
  key: number;
  loc: "s" | "m" | "e"; // start, middle, end
}

export interface ElevationDataResponse {
  after_end_lat: number;
  after_end_lng: number;
  data: ElevationDataPoint[];
  distance_meters: number;
  end_lat: number;
  end_lng: number;
  previous_start_lat: number;
  previous_start_lng: number;
  start_lat: number;
  start_lng: number;
}
